/**
 * SA-1124 (spec §4.2, §4.5): the register's server-side sort. The keys a
 * column header may send are the server's `orders.COLUMN_SORTS`, each with
 * the direction its first click takes; both sides are pinned by
 * shared/register-sort-keys.json. Pure: no store, no DOM.
 */
export type SortDir = 'asc' | 'desc'

export interface RegisterSort {
  key: string
  dir: SortDir
}

export const COLUMN_SORTS = {
  reference: 'asc', company: 'asc', contact_name: 'asc', step: 'asc', status: 'asc',
  waiting_on: 'asc', quote: 'desc', updated: 'desc', assigned_to: 'asc', needed_window: 'asc',
  // SA-1183: the role columns, the route and the owner.
  contractual_partner: 'asc', cargo_agent: 'asc', route: 'asc', owner: 'asc',
} as const satisfies Readonly<Record<string, SortDir>>

/** The keys the server accepts for a column sort (F8). */
export type ColumnSortKey = keyof typeof COLUMN_SORTS

/** The sorts no column names; they take no direction. */
export const LEGACY_SORTS: readonly string[] = ['recent', 'oldest', 'amount', 'delivery_date', 'priority']

export const DEFAULT_SORT = 'recent'

/** SA-1183: a custom module's register sorts, key → first click (`RegisterViewEntry.sorts`).
 *  The server sorts by nothing a viewer may not use (spec R5). */
export type ModuleSorts = Readonly<Record<string, SortDir>>

const NO_MODULE_SORTS: ModuleSorts = {}

const hasOwn = (map: object, key: string) => Object.prototype.hasOwnProperty.call(map, key)

export function isColumnSort(key: string | null | undefined): key is ColumnSortKey {
  return !!key && hasOwn(COLUMN_SORTS, key)
}

/** A key the server sorts a column by: a core column's, or a module's (SA-1183). */
export function isSortKey(key: string | null | undefined, extra: ModuleSorts = NO_MODULE_SORTS): key is string {
  return isColumnSort(key) || (!!key && hasOwn(extra, key))
}

/** A column's first click (SA-1185): the register's direction for a server key, a module
 *  key's own (SA-1183), else ascending. A header's cycle is `clientSort.cycleSort` from this;
 *  a register column's key is typed `ColumnSortKey` (`registerColumns.ServerSortColumn`, SA-1193). */
export function firstClick(key: string, extra: ModuleSorts = NO_MODULE_SORTS): SortDir {
  if (isColumnSort(key)) return COLUMN_SORTS[key]
  return hasOwn(extra, key) ? extra[key] : 'asc'
}

/** A sort read from the URL: a column key (a module's too, SA-1183) with its direction (the
 *  first click's when absent or unreadable), a legacy key alone, anything else the
 *  default — so a stale link never sends the server a key it would refuse. */
export function readSort(
  sort: string | null, dir: string | null, extra: ModuleSorts = NO_MODULE_SORTS,
): { sortBy: string; sortDir: SortDir | null } {
  if (isSortKey(sort, extra)) return { sortBy: sort, sortDir: dir === 'asc' || dir === 'desc' ? dir : firstClick(sort, extra) }
  if (sort && LEGACY_SORTS.includes(sort)) return { sortBy: sort, sortDir: null }
  return { sortBy: DEFAULT_SORT, sortDir: null }
}

/** The column the list is sorted by, or null for a sort no column names. */
export function columnSort(sortBy: string, sortDir: SortDir | null, extra: ModuleSorts = NO_MODULE_SORTS): RegisterSort | null {
  return isSortKey(sortBy, extra) && sortDir ? { key: sortBy, dir: sortDir } : null
}

/** SA-1184: the column sorts the built-in register's headers offer. Pinned to
 *  `RegisterView`'s columns by registerView.test.tsx, as a module view's
 *  `sortKeys` is pinned to its own table. Status counts: the page shows the
 *  Status column whenever the list sorts by it. */
export const REGISTER_SORT_KEYS: readonly ColumnSortKey[] = [
  'reference', 'company', 'contact_name', 'contractual_partner', 'cargo_agent', 'route', 'needed_window',
  'step', 'status', 'owner', 'waiting_on', 'quote', 'updated',
]

/** SA-1184 (ruling R2): whether the list's sort survives a switch to a table
 *  that sorts by `targetKeys`. A sort no column names (the default, Priority)
 *  always survives; a column sort, a module's included (SA-1183), survives only
 *  where that column sorts. */
export function sortSurvives(sortBy: string, targetKeys: readonly string[], extra: ModuleSorts = NO_MODULE_SORTS): boolean {
  return !isSortKey(sortBy, extra) || targetKeys.includes(sortBy)
}
